/* eslint-disable react/jsx-curly-newline */
/* eslint-disable react/jsx-one-expression-per-line */
import React, { useContext, useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import { useHistory } from 'react-router-dom';
import LoadingOverlay from 'react-loading-overlay';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@material-ui/core';
import { useRecoilState, useSetRecoilState } from 'recoil';
import AuthenticatedContext from '../../context/AuthenticatedContext';
import { pathnames } from '../../api';
import Equipment from '../../models/data/Equipment';
import { requestCountEquipment, requestEquipment } from '../../api/equipment';
import Paging from '../paging';
import useStyles, { headerWidth } from '../../css/style';
import {
  backPageFlg,
  eqListPageInfo,
  pageTitle,
} from '../../context/PageRecoil';
import { routeName } from '../../App';
import DataDisp from './datadisp';
import useErrorHandling from '../errorCheck';

/**
 * 設備一覧画面
 */
const Eqlist: React.FunctionComponent = () => {
  // Recoilから過去ページ状態を取得
  const [pageInfo, setPageInfo] = useRecoilState(eqListPageInfo);
  const [backFlg, setBackFlg] = useRecoilState(backPageFlg);
  const setPageTitle = useSetRecoilState(pageTitle);
  React.useEffect(() => {
    return setPageTitle({ pageTitle: '設備一覧' }); // ページのタイトルを設定
  }, [setPageTitle]);
  const { errorCheck } = useErrorHandling(); // エラー処理

  const [loading, setLoading] = useState(true); // ロード画面の状態管理
  const history = useHistory();
  const classes = useStyles(); // スタイリングを取得
  const authContext = useContext(AuthenticatedContext); // ログインユーザの情報を取得
  const [data, setData] = useState<Equipment[]>([]); // 設備情報
  const [scroll, setScroll] = useState<number>(0); // テーブルのスクロール位置

  // APIのURL
  const urlEquipment = history.createHref({ pathname: pathnames.equipment }); // 設備一覧取得
  const urlCountEquipment = history.createHref({
    pathname: pathnames.equipmentCount,
  }); // データ数の取得
  const [searchTerm, setSearchTerm] = useState(''); // 検索ワードの保存用stateを追加
  const [searchWord, setSearchWord] = useState(''); // 検索するときに使うstateを追加
  const containerRef = React.useRef<HTMLDivElement>(null);

  // ページング用の変数
  const [totalData, setTotalData] = useState<number>(0); // データ数
  const [currentPage, setCurrentPage] = useState<number>(0); // 現在のページ
  const perPage = 20; // 一覧一ページあたりの表示件数
  // ソート用の変数
  type SortConfigType = { key: string; direction: string };
  const [sortConfig, setSortConfig] = React.useState<SortConfigType>({
    key: '',
    direction: '',
  });

  const headerCellProps = [
    {
      width: headerWidth.equipmentId,
      sortKey: 'equipmentId',
      text: '設備ID',
      className: classes.header,
    },
    {
      width: headerWidth.eqButton,
      className: classes.header,
      // 空のヘッダはsortKeyやlabelを設定しない（ソートボタンは作られない）
    },
    {
      width: headerWidth.officeName,
      sortKey: 'officeName',
      text: '事業所名',
      className: classes.header,
    },
    {
      width: headerWidth.institution,
      sortKey: 'institution',
      text: '建屋名',
      className: classes.header,
    },
    {
      width: headerWidth.eqLocation,
      sortKey: 'eqLocation',
      text: '設備設置場所',
      className: classes.header,
    },
    {
      width: headerWidth.equipmentName,
      sortKey: 'equipmentName',
      text: '設備名',
      className: classes.header,
    },
    {
      width: headerWidth.eqNo,
      sortKey: 'eqNo',
      text: '設備No',
      className: classes.header,
    },
    {
      width: headerWidth.eq1Name,
      sortKey: 'eq1Name',
      text: '設備分類',
      className: classes.header,
    },
    {
      width: headerWidth.secName,
      sortKey: 'secName',
      text: '担当部署',
      className: classes.header,
    },
  ];

  // 画面遷移で送られた情報を設定する
  const locationSet = () => {
    // 初期値のままの場合
    if (currentPage === 0) {
      setCurrentPage(1);
    }
    if (backFlg.eqList) {
      setSearchTerm(pageInfo.serchWord);
      setSearchWord(pageInfo.serchWord);
      setSortConfig({
        key: pageInfo.sortKey,
        direction: pageInfo.sortDirection,
      });
      setCurrentPage(pageInfo.page);
      // 一覧のテーブルがあるか確認
      if (containerRef.current) {
        setScroll(pageInfo.scrollPosition);
      }
      setBackFlg({ ...backFlg, eqList: false });
    }
  };

  useEffect(() => {
    locationSet();
  }, []);

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = scroll;
    }
  }, [data]);

  // 一覧のページ変更処理
  const pageChange = (num: number) => {
    setScroll(0);
    setCurrentPage((prevPage) => {
      let newPage = prevPage + num;
      const tempTotalPages = Math.ceil(totalData / perPage);
      const totalPages = tempTotalPages > 0 ? tempTotalPages : 1;

      if (newPage < 1) newPage = 1;
      if (newPage > totalPages) newPage = totalPages;
      return newPage;
    });
  };

  // 事業所検索の検索文字列を変更
  const changeSearchTerm = (event: {
    target: { value: React.SetStateAction<string> };
  }) => {
    setSearchTerm(event.target.value);
  };

  // 検索ボタンクリック時の処理を追加
  const handleSearchClick = () => {
    pageChange(1 - currentPage); // はじめのページに戻る
    setScroll(0); // 表のスクロールバーをリセット
    setSearchWord(searchTerm);
  };

  // ページ遷移時の処理
  const movePage = (route: string) => {
    const tempScroll = containerRef.current
      ? containerRef.current.scrollTop
      : 0;
    setPageInfo({
      page: currentPage,
      scrollPosition: tempScroll,
      sortKey: sortConfig.key,
      sortDirection: sortConfig.direction,
      serchWord: searchWord,
    });
    history.push(route);
  };

  // ソート機能の関数など
  const sortStart = (key: string) => {
    pageChange(1 - currentPage); // はじめのページに戻る
    setScroll(0); // 表のスクロールバーをリセット
    let direction = 'asc';
    if (
      sortConfig &&
      sortConfig.key === key &&
      sortConfig.direction === 'asc'
    ) {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // 設備情報の取得
  useEffect(() => {
    if (currentPage !== 0) {
      setLoading(true); // 非同期処理前にsetLoading(true)
      Promise.all([
        requestCountEquipment(urlCountEquipment, {
          officeName: searchWord,
        }),
        requestEquipment(urlEquipment, {
          number: perPage,
          page: currentPage,
          sortKey: sortConfig.key,
          sortDirection: sortConfig.direction,
          officeName: searchWord,
        }),
      ])
        .then((responses) => {
          setTotalData(responses[0].data); // 件数
          setData(responses[1].data); // 設備情報
        })
        .catch((error) => {
          errorCheck(error); // エラー内容を確認
        })
        .finally(() => {
          setLoading(false); // 非同期処理後にsetLoading(false)
        });
    }
  }, [currentPage, sortConfig, searchWord]);

  return (
    <LoadingOverlay
      active={loading}
      className={classes.load}
      spinner
      text="データを読み込んでいます..."
    >
      <div className={classes.root}>
        <Button
          variant="primary"
          className="my-2"
          onClick={() => history.push(routeName.menu)}
        >
          戻る
        </Button>
        <DataDisp
          userdata={authContext}
          searchTerm={searchTerm}
          changeSearchTerm={changeSearchTerm}
          handleSearchClick={handleSearchClick}
        />
        <div className={`${classes.paging} ${classes.eqListWidth}`}>
          <Paging
            totalData={totalData}
            currentPage={currentPage}
            perPage={perPage}
            onPageChange={pageChange}
          />
        </div>
        <TableContainer
          className={`${classes.container} ${classes.eqListHeight} ${classes.eqListWidth}`}
          ref={containerRef}
          component={Paper}
        >
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {/* ヘッダのセルを生成 */}
                {headerCellProps.map(({ width, sortKey, text, className }) => (
                  <TableCell width={width} className={className}>
                    {sortKey != null && (
                      <Button
                        variant="link"
                        onClick={() => sortStart(sortKey)}
                        className={classes.linkButton}
                      >
                        {text}
                        {sortConfig &&
                          sortConfig.key === sortKey &&
                          (sortConfig.direction === 'asc' ? '▲' : '▼')}
                      </Button>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((d, index) => (
                <TableRow
                  key={d.equipmentId}
                  className={index % 2 === 0 ? classes.evenRow : classes.oddRow}
                >
                  <TableCell className={`${classes.cell} ${classes.numRow}`}>
                    {d.equipmentId}
                  </TableCell>
                  <TableCell className={`${classes.cell} ${classes.buttonRow}`}>
                    <Button
                      variant="outline-primary"
                      className="mb-1"
                      onClick={() =>
                        movePage(
                          `${routeName.deviceList}/${
                            d.equipmentId ? d.equipmentId : ''
                          }`,
                        )
                      }
                    >
                      機器情報
                    </Button>
                    <br />
                    <Button
                      variant="outline-primary"
                      onClick={() =>
                        movePage(
                          `${routeName.eqMaintMenu}/${
                            d.equipmentId ? d.equipmentId : ''
                          }`,
                        )
                      }
                    >
                      設備情報
                    </Button>
                  </TableCell>
                  <TableCell className={`${classes.cell} ${classes.stringRow}`}>
                    {d.officeName}
                  </TableCell>
                  <TableCell className={`${classes.cell} ${classes.stringRow}`}>
                    {d.institution}
                  </TableCell>
                  <TableCell className={`${classes.cell} ${classes.stringRow}`}>
                    {d.eqLocation}
                  </TableCell>
                  <TableCell className={`${classes.cell} ${classes.stringRow}`}>
                    {d.equipmentName}
                  </TableCell>
                  <TableCell className={`${classes.cell} ${classes.stringRow}`}>
                    {d.eqNo}
                  </TableCell>
                  <TableCell className={`${classes.cell} ${classes.stringRow}`}>
                    {d.eq1Name}
                  </TableCell>
                  <TableCell className={`${classes.cell} ${classes.stringRow}`}>
                    {d.secName}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
    </LoadingOverlay>
  );
};

export default Eqlist;
